import {testIdProps} from '@github-ui/test-id-props'
import {Label, type ProgressBarProps, Text, useTheme} from '@primer/react'

import styles from './progress-bar.module.css'
import type {ProgressBarAdditionalProps} from './types'

export const ProgressBarRing = ({
  color,
  completed,
  name,
  percentCompleted,
  total,
}: ProgressBarAdditionalProps & Pick<ProgressBarProps, 'color'>) => {
  const {theme} = useTheme()
  const strokeWidth = 20
  const radius = Math.floor(100 - strokeWidth) / 2
  const lineCapDiameter = 15
  const lineCapRadius = Math.ceil(lineCapDiameter / 2)
  const circleRotateAdjustment = -75
  const percentCompletedCalculated = percentCompleted >= 100 ? 100 : Math.max(percentCompleted - lineCapRadius, 1)

  const RingBackground = () => (
    <circle
      className={styles.circle}
      cx="50"
      cy="50"
      r={radius}
      fill="none"
      stroke={theme?.colors.border.default}
      strokeWidth={strokeWidth}
    />
  )

  const RingProgressShade = () => (
    <circle
      className={styles.circleProgressShade}
      cx="50"
      cy="50"
      r="20"
      fill="none"
      pathLength="100"
      stroke={color}
      strokeDasharray="100"
      strokeDashoffset={percentCompletedCalculated === 100 ? 0 : 100 - percentCompletedCalculated - lineCapRadius}
      strokeWidth="40"
      style={{
        transform: `rotate(${circleRotateAdjustment - lineCapDiameter}deg)`,
      }}
    />
  )

  const RingProgress = () => (
    <circle
      className={styles.circleProgress}
      cx="50"
      cy="50"
      r={radius}
      fill="none"
      pathLength="100"
      stroke={color}
      strokeDasharray="100"
      strokeDashoffset={100 - percentCompletedCalculated}
      strokeLinecap="round"
      strokeWidth={strokeWidth}
      style={{
        transform: `rotate(${circleRotateAdjustment}deg)`,
      }}
    />
  )

  return (
    <Label aria-label={name} className={styles.containerRing} variant="secondary" {...testIdProps('progress-bar-ring')}>
      <svg className={styles.ringSvg} height="1em" width="1em" role="presentation" viewBox="0 0 100 100">
        {percentCompleted > 0 ? <RingProgressShade /> : null}

        <RingBackground />

        {percentCompleted > 0 ? <RingProgress /> : null}
      </svg>

      <Text>
        {completed} / {total}
      </Text>
    </Label>
  )
}

try{ ProgressBarRing.displayName ||= 'ProgressBarRing' } catch {}
try{ RingBackground.displayName ||= 'RingBackground' } catch {}
try{ RingProgressShade.displayName ||= 'RingProgressShade' } catch {}
try{ RingProgress.displayName ||= 'RingProgress' } catch {}